import React, { useEffect, useState, useCallback } from "react";
import { api, formatDate } from "../../lib/api";
import { Trash2, Mail, ShoppingBag, RotateCcw, Info } from "lucide-react";
import { toast } from "sonner";

const daysAgo = (iso) => {
  if (!iso) return null;
  try {
    const ms = Date.now() - new Date(iso).getTime();
    const days = ms / (1000 * 60 * 60 * 24);
    return days;
  } catch {
    return null;
  }
};

const daysRemaining = (iso, retentionDays) => {
  const elapsed = daysAgo(iso);
  if (elapsed == null) return null;
  return Math.max(0, retentionDays - elapsed);
};

export default function TrashTab() {
  const [items, setItems] = useState([]);
  const [retention, setRetention] = useState(30);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/trash");
      setItems(data.items || []);
      setRetention(data.retention_days || 30);
    } catch {
      toast.error("Failed to load trash");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const restore = async (item) => {
    try {
      const endpoint =
        item.kind === "message"
          ? `/admin/trash/messages/${item.id}/restore`
          : `/admin/trash/orders/${item.id}/restore`;
      await api.post(endpoint);
      toast.success(`Restored ${item.kind === "message" ? "message" : "order"}`);
      setItems((prev) => prev.filter((x) => !(x.kind === item.kind && x.id === item.id)));
    } catch {
      toast.error("Restore failed");
    }
  };

  const purge = async (item) => {
    if (
      !confirm(
        `Permanently delete this ${item.kind}?\n\n${item.title}\n\nThis cannot be undone — it will not be auto-restored.`,
      )
    )
      return;
    try {
      const endpoint =
        item.kind === "message"
          ? `/admin/trash/messages/${item.id}`
          : `/admin/trash/orders/${item.id}`;
      await api.delete(endpoint);
      toast.success("Permanently deleted");
      setItems((prev) => prev.filter((x) => !(x.kind === item.kind && x.id === item.id)));
    } catch {
      toast.error("Delete failed");
    }
  };

  const emptyAll = async () => {
    if (items.length === 0) return;
    if (
      !confirm(
        `Permanently delete ALL ${items.length} item${items.length === 1 ? "" : "s"} in the trash?\n\nThis cannot be undone.`,
      )
    )
      return;
    let failed = 0;
    for (const it of items) {
      try {
        const endpoint =
          it.kind === "message"
            ? `/admin/trash/messages/${it.id}`
            : `/admin/trash/orders/${it.id}`;
        await api.delete(endpoint);
      } catch {
        failed += 1;
      }
    }
    if (failed === 0) toast.success("Trash emptied");
    else toast.error(`${failed} item${failed === 1 ? "" : "s"} could not be deleted`);
    await load();
  };

  return (
    <div data-testid="trash-tab">
      <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <p className="font-script text-2xl" style={{ color: "var(--text-accent)" }}>
            recently deleted
          </p>
          <h1 className="font-display text-4xl" style={{ color: "var(--text-primary)" }}>
            Trash
          </h1>
        </div>
        {items.length > 0 && (
          <button
            onClick={emptyAll}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold"
            style={{ border: "1px solid var(--border-soft)", color: "var(--brand-orange)", backgroundColor: "#fff" }}
            data-testid="empty-trash-button"
          >
            <Trash2 size={14} />
            Empty trash
          </button>
        )}
      </header>

      <div
        className="rounded-xl px-4 py-3 mb-6 flex items-start gap-3 text-sm"
        style={{ backgroundColor: "rgba(74,93,78,0.1)", color: "var(--text-primary)" }}
      >
        <Info size={18} style={{ color: "var(--brand-sage)", flexShrink: 0, marginTop: 1 }} />
        <p>
          Items moved to the trash are kept here for <strong>{retention} days</strong> and then
          automatically deleted. Restore anything by mistake — or purge it sooner with “Delete forever”.
        </p>
      </div>

      {loading ? (
        <p className="font-script text-xl" style={{ color: "var(--text-secondary)" }}>
          loading…
        </p>
      ) : items.length === 0 ? (
        <div
          className="bg-white rounded-2xl p-10 text-center"
          style={{ border: "1px dashed var(--border-soft)" }}
          data-testid="trash-empty-state"
        >
          <Trash2 size={36} className="mx-auto" style={{ color: "var(--brand-peach)" }} />
          <p className="font-script text-2xl mt-3" style={{ color: "var(--text-accent)" }}>
            all tidy
          </p>
          <p className="text-sm mt-1" style={{ color: "var(--text-secondary)" }}>
            Nothing in your trash right now.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((item) => {
            const remaining = daysRemaining(item.deleted_at, retention);
            const remainingLabel =
              remaining == null
                ? ""
                : remaining < 1
                ? "purges in less than a day"
                : `purges in ${Math.ceil(remaining)} day${Math.ceil(remaining) === 1 ? "" : "s"}`;
            const Icon = item.kind === "message" ? Mail : ShoppingBag;
            return (
              <article
                key={`${item.kind}-${item.id}`}
                className="bg-white rounded-2xl p-5"
                style={{ border: "1px solid var(--border-soft)" }}
                data-testid={`trash-${item.kind}-${item.id}`}
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex items-start gap-3 min-w-0 flex-1">
                    <div
                      className="rounded-full p-2 flex-shrink-0"
                      style={{ backgroundColor: "var(--bg-secondary)", color: "var(--brand-sage)" }}
                    >
                      <Icon size={16} />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3
                          className="font-display text-lg leading-tight"
                          style={{ color: "var(--text-primary)" }}
                        >
                          {item.title}
                        </h3>
                        <span
                          className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full"
                          style={{ backgroundColor: "rgba(160,82,45,0.1)", color: "var(--text-accent)" }}
                        >
                          {item.kind}
                        </span>
                      </div>
                      {item.subtitle && (
                        <p
                          className="text-xs mt-0.5 break-all"
                          style={{ color: "var(--text-secondary)" }}
                        >
                          {item.subtitle}
                        </p>
                      )}
                      {item.preview && (
                        <p
                          className="text-sm mt-2 line-clamp-2"
                          style={{ color: "var(--text-primary)", overflowWrap: "anywhere" }}
                        >
                          {item.preview}
                        </p>
                      )}
                      <p className="text-xs mt-2" style={{ color: "var(--text-secondary)" }}>
                        Deleted {formatDate(item.deleted_at)} · {remainingLabel}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="flex gap-2 mt-3 justify-end">
                  <button
                    onClick={() => restore(item)}
                    className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-semibold"
                    style={{ border: "1px solid var(--border-soft)", color: "var(--brand-sage)" }}
                    data-testid={`restore-${item.kind}-${item.id}`}
                  >
                    <RotateCcw size={12} />
                    Restore
                  </button>
                  <button
                    onClick={() => purge(item)}
                    className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-semibold"
                    style={{ border: "1px solid var(--border-soft)", color: "var(--brand-orange)" }}
                    data-testid={`purge-${item.kind}-${item.id}`}
                  >
                    <Trash2 size={12} />
                    Delete forever
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}
